import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { Redirect, useHistory } from 'react-router';
import { toast } from 'sonner';
import { UserViewModel } from '../../../extViewmodels';
import { useErrorMessages, useTextMessaging, useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { useResolvePhoneNumber, useTelephonyTextSettingsQuery } from '../../../queries';
import {
	AppDrawer,
	AppDrawerContent,
	AppDrawerSeparator,
	AppDrawerToolbar,
	AppDrawerToolbarButton,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
	useAppDrawerContext,
} from '../../components-v2/Drawer/AppDrawer';
import { Column, Page } from '../../components-v2/PageLayout';
import { TextArea } from '../../components-v2/TextArea';
import { AppBarNavigationItemPortalDestinationId } from '../../components/AppBar';
import { Button } from '../../components/Button';
import { DangerMessage } from '../../components/errorMessages/DangerMessage';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Modal } from '../../components/Modal';
import { Portal } from '../../components/Portal';
import { AssigneeIcon } from '../../components/svgs/icons/AssigneeIcon';
import { Conversations } from '../../components/texting/Conversations';
import { ContactSection } from '../../components/texting/Conversations/components/ContactSection';
import {
	TextMessageConversationsContextProvider,
	useTextMessageConversationsContext,
} from '../../components/texting/Conversations/context';
import { Setup } from '../../components/texting/Setup';
import { TextingProvidersMigration } from '../../components/texting/TextingProvidersMigration';
import { TextRegistrationInProgressModal } from '../../components/texting/TextRegistrationInProgressModal';
import { TextRegistrationRequiredModal } from '../../components/texting/TextRegistrationRequiredModal';
import { Toggle } from '../../components/Toggle';
import { grayIconFill } from '../../styles/colors';
import { baseStyleSheet } from '../../styles/styles';
import { ContactInfo } from '../PeopleV2/PeopleContainer/AppDrawerContents/ContactInfo/ContactInfo';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';
import { styleSheet } from './styles';
import { useTextingCampaignRegistrationContext } from './TextingCampaignRegistrationPrompt/context';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

export const TextingBase: React.FC<IProps> = () => {
	const errorMessages = useErrorMessages();
	const logger = useEventLogging();
	const textMessagingVM = useTextMessaging();
	const userSession = useUserSession();

	const history = useHistory();

	const userViewModel = useRef(new UserViewModel(userSession, userSession.user)).current;

	const [showManageTextAutoReply, setShowManageTextAutoReply] = React.useState(false);
	const [textAutoReplyEnabled, setTextAutoReplyEnabled] = React.useState(false);
	const [textAutoReplyAfterHoursEnabled, setTextAutoReplyAfterHoursEnabled] = React.useState(false);
	const [textAutoReplyMessage, setTextAutoReplyMessage] = React.useState('');
	const [textAutoReplyAfterHoursMessage, setTextAutoReplyAfterHoursMessage] = React.useState('');
	const [saveButtonEnabled, setSaveButtonEnabled] = React.useState(true);
	const [redirectTo, setRedirectTo] = React.useState<string>(null);
	const {
		showRegistrationRequiredModal,
		isCampaignRegistrationRequired,
		showRegistrationInProgressModal,
		isRegistrationInProgress,
		setShowRegistrationRequiredModal,
		setShowRegistrationInProgressModal,
		isLoading,
		textSettings,
		checkShowRegistration,
	} = useTextingCampaignRegistrationContext();

	// eslint-disable-next-line react-hooks/exhaustive-deps
	React.useEffect(checkShowRegistration, []);

	const [toggleProvidersMigrationModal, setProvidersMigrationModal] = React.useState(false);

	const shouldRenderMigrationWarning = textMessagingVM?.phoneNumberOrder?.isUsingTemporarySendingNumber;
	const phoneNumberId = textMessagingVM?.phoneNumberOrder?.sendingPhoneNumberId;

	const resolvePhoneNumberQuery = useResolvePhoneNumber({
		enabled: Boolean(shouldRenderMigrationWarning && phoneNumberId),
		phoneNumberId,
	});
	// turn off caching as we don't want to read a stale value and block the user from texting after reg. is successful
	const telephonyTextSettingsQuery = useTelephonyTextSettingsQuery({
		gcTime: 0,
	});

	React.useEffect(() => {
		if (
			textMessagingVM.isLoaded &&
			textMessagingVM.number == null &&
			telephonyTextSettingsQuery.data &&
			!telephonyTextSettingsQuery.data.canRequestTextingPhoneNumber
		) {
			history.push('/texting/registration');
		}
	}, [telephonyTextSettingsQuery.data, history, textMessagingVM.isLoaded, textMessagingVM.number]);

	useEffect(() => {
		if (textMessagingVM.isLoaded === undefined) {
			textMessagingVM.getUserPhone();
		}
	}, [textMessagingVM]);

	useEffect(() => {
		setTextAutoReplyEnabled(userViewModel.toJs().textAutoReply?.enabled ?? false);
		setTextAutoReplyAfterHoursEnabled(userViewModel.toJs().textAutoReply?.replyOutsideOfSendIntervals ?? false);
		setTextAutoReplyMessage(
			userViewModel.toJs().textAutoReply?.message ??
				'[Auto Reply] Thanks for your text. I am out of the office right now and will respond as soon as I can.'
		);
		setTextAutoReplyAfterHoursMessage(userViewModel.toJs().textAutoReply?.afterHoursMessage ?? '');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userViewModel.isLoaded]);

	useEffect(() => {
		setSaveButtonEnabled(!!textAutoReplyMessage && textAutoReplyMessage.length > 0);
	}, [textAutoReplyMessage]);

	const setupComplete = async () => {
		try {
			return await textMessagingVM.getUserPhone();
		} catch (err) {
			logger.logApiError('GetUserPhone-Error', err);

			errorMessages.push(err);
		}
	};

	const onSaveClicked = async () => {
		try {
			await userViewModel.updateTextAutoReplySettings({
				enabled: textAutoReplyEnabled,
				message: textAutoReplyMessage,
				afterHoursMessage: textAutoReplyAfterHoursMessage,
				replyOutsideOfSendIntervals: textAutoReplyAfterHoursEnabled,
			});

			toast.success('Successfully updated text out of office response.');

			setShowManageTextAutoReply(false);
		} catch (err) {
			errorMessages.push(err);
		}
	};

	const onCancelClicked = () => {
		toggleShowManageTextAutoReplyModal();
		setTextAutoReplyEnabled(userViewModel.toJs().textAutoReply?.enabled ?? false);
		setTextAutoReplyAfterHoursEnabled(userViewModel.toJs().textAutoReply?.replyOutsideOfSendIntervals ?? false);
		setTextAutoReplyMessage(
			userViewModel.toJs().textAutoReply?.message ??
				'[Auto Reply] Thanks for your text. I am out of the office right now and will respond as soon as I can.'
		);
		setTextAutoReplyAfterHoursMessage(userViewModel.toJs().textAutoReply?.afterHoursMessage ?? '');
	};

	const toggleShowManageTextAutoReplyModal = () => {
		setShowManageTextAutoReply(!showManageTextAutoReply);
	};

	const onCloseRegistrationWarningClicked = () => {
		setShowRegistrationRequiredModal(false);
	};

	const onCloseRegistrationInProgressClicked = () => {
		setShowRegistrationInProgressModal(false);
	};

	const onTextAreaUpdated = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setTextAutoReplyMessage(e.target.value);
	};

	const onAfterHoursTextAreaUpdated = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setTextAutoReplyAfterHoursMessage(e.target.value);
	};

	if (redirectTo) {
		return <Redirect from='/texting' to={redirectTo} push={true} />;
	}

	return (
		<TextMessageConversationsContextProvider>
			<Portal destination={AppBarNavigationItemPortalDestinationId}>
				<h1 className={css(baseStyleSheet.breadcrumbTitle)}>Texting</h1>
			</Portal>
			<Page styles={[styleSheet.pageContainer]}>
				{shouldRenderMigrationWarning || textMessagingVM.phoneNumberOrder?.creator?.id === userSession.user.id ? (
					<div className={css(styleSheet.autoReply)}>
						{shouldRenderMigrationWarning ? (
							<DangerMessage type='warning'>
								Your texts are being sent with a temporary number while we are migrating providers.{' '}
								<button
									type='button'
									className={css(baseStyleSheet.brandLink)}
									onClick={() => setProvidersMigrationModal(true)}
								>
									<strong className={css(baseStyleSheet.fontBold)}>Read More</strong>
								</button>
							</DangerMessage>
						) : (
							<div />
						)}
						{textMessagingVM.phoneNumberOrder?.creator?.id === userSession.user.id && (
							<div className={css(styleSheet.autoReplyContainer)}>
								<Button className={css(styleSheet.manageButton)} onClick={toggleShowManageTextAutoReplyModal}>
									<span>Manage Automatic Responses</span>
								</Button>
							</div>
						)}
					</div>
				) : null}
				<div className={css(styleSheet.wrapper)}>
					{textMessagingVM.isLoading ||
					!textMessagingVM.isLoaded ||
					telephonyTextSettingsQuery.isPending ||
					isLoading ? (
						<LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />
					) : textMessagingVM.id ? (
						<Conversations
							requiresCampaignRegistration={isCampaignRegistrationRequired}
							isRegistrationInProgress={isRegistrationInProgress}
						/>
					) : (
						<Setup onSetupComplete={setupComplete} />
					)}
				</div>

				<Modal
					isOpen={toggleProvidersMigrationModal}
					onRequestClose={() => setProvidersMigrationModal(false)}
					useDefaultHeader={true}
				>
					<TextingProvidersMigration temporaryNumber={resolvePhoneNumberQuery?.data?.number} />
				</Modal>
				<Modal isOpen={showManageTextAutoReply} useDefaultHeader={true} onRequestClose={onCancelClicked}>
					<div className={css(styleSheet.modal)}>
						<div className={css(styleSheet.modalTitle)}>Automatic &quot;Out of Office&quot; Response</div>
						<div className={css(styleSheet.modalContent)}>
							<Toggle
								id='outOfOfficeToggle'
								text='Enable Auto Reply'
								uncheckedColor={grayIconFill}
								isOn={textAutoReplyEnabled}
								onToggleCheckChanged={setTextAutoReplyEnabled}
							/>
							{textAutoReplyEnabled && (
								<TextArea
									autoComplete='off'
									styles={[styleSheet.autoReplyMessageTextField]}
									onChange={onTextAreaUpdated}
									value={textAutoReplyMessage}
								/>
							)}

							<Toggle
								id='outOfOfficeToggleAfterHours'
								text='Enable Auto Reply after business hours'
								uncheckedColor={grayIconFill}
								isOn={textAutoReplyAfterHoursEnabled}
								onToggleCheckChanged={setTextAutoReplyAfterHoursEnabled}
							/>

							{textAutoReplyAfterHoursEnabled && (
								<TextArea
									autoComplete='off'
									styles={[styleSheet.autoReplyMessageTextField]}
									onChange={onAfterHoursTextAreaUpdated}
									value={textAutoReplyAfterHoursMessage}
									placeholder='If left blank we will fallback to the message above.'
								/>
							)}
							<div className={css(styleSheet.footer)}>
								<Button label='Save' size='normal' disabled={!saveButtonEnabled} onClick={onSaveClicked} />
								<Button label='Cancel' size='normal' kind='reverse' onClick={onCancelClicked} />
							</div>
						</div>
					</div>
				</Modal>
				<TextRegistrationRequiredModal
					showRegistrationRequiredModal={showRegistrationRequiredModal}
					telephonyTextSettings={textSettings}
					onRequestClose={onCloseRegistrationWarningClicked}
				/>
				<TextRegistrationInProgressModal
					showRegistrationRequiredModal={showRegistrationInProgressModal}
					onRequestClose={onCloseRegistrationInProgressClicked}
				/>
				<AppDrawer>
					<StickyAppDrawerContent value='ai-assistant'>
						<DefaultUniversalAssistantChat />
					</StickyAppDrawerContent>
					<AppDrawerContent value='contact-view'>
						<ContactView setRedirectTo={setRedirectTo} />
					</AppDrawerContent>
					<AppDrawerToolbar>
						<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
						<AppDrawerSeparator />
						<ContactViewDrawerButton />
					</AppDrawerToolbar>
				</AppDrawer>
			</Page>
		</TextMessageConversationsContextProvider>
	);
};

export const Texting = observer(TextingBase);

const ContactView = observer(({ setRedirectTo }: { setRedirectTo: (redirectTo: string) => void }) => {
	const {
		selectedConversation,
		setRecipients,
		setSelectedRecipient,
		setSelectedRecipientIndex,
		setShowNewConversationSearch,
	} = useTextMessageConversationsContext();
	const [refresh, setRefresh] = React.useState<boolean>(false);

	useEffect(() => {
		if (refresh) {
			// Reset the context states to allow for new contact creation
			// This mirrors the handleCreateConversationClick logic from the main Conversations component
			setRecipients([]);
			setSelectedRecipient(null);
			setSelectedRecipientIndex(null);
			setShowNewConversationSearch(false);
			setRefresh(false);
		}
	}, [refresh, setRecipients, setSelectedRecipient, setSelectedRecipientIndex, setShowNewConversationSearch]);

	const contactId = selectedConversation?.toNumbers?.[0]?.contact?.id;

	return contactId ? (
		<ContactInfo contactId={contactId} />
	) : (
		<Column styles={[styleSheet.contactWrapper]}>
			<ContactSection setRedirectTo={setRedirectTo} onSuccess={() => setRefresh(true)} />
		</Column>
	);
});

// One off component, created to leverage context provider
const ContactViewDrawerButton = () => {
	const { selectedRecipient, selectedConversation } = useTextMessageConversationsContext();
	const { value, setValue } = useAppDrawerContext();

	useEffect(() => {
		if (!value && (selectedConversation || selectedRecipient)) {
			setValue('contact-view');
		} else if (value === 'contact-view' && !selectedConversation && !selectedRecipient) {
			setValue(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedConversation, selectedRecipient]);

	return (
		<AppDrawerToolbarButton
			value='contact-view'
			disabled={!(selectedRecipient || selectedConversation)}
			tooltip='Contact preview'
		>
			<AssigneeIcon />
		</AppDrawerToolbarButton>
	);
};
